import {
  atom,
  atomFamily,
  selector,
  selectorFamily,
  useRecoilValue,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
  useRecoilRefresher_UNSTABLE,
} from "recoil";
import { localStorageEffect } from "./effects";
import { NEEDS_REVIEW_STATUS } from "../constants";

const DEFAULT_SELECTED_MERGE_PROPOSAL = { id: "" };

const sessionApiClientAtom = atom({
  key: "apiClient",
  default: null,
});

const currentSessionAtom = atom({
  key: "currentSession",
  // get initial state from local storage to enable user to stay logged in
  default: {},
});


const selectedSubjectAtom = atom({
  key: "selectedSubject",
  default: { name: "" },
});

const selectedMergeProposalAtom = atom({
  key: "selectedMergeProposal",
  default: DEFAULT_SELECTED_MERGE_PROPOSAL,
});

// The purpose of this atom is to force a re-evaluation of the atoms/selectors depending on it.
// This atom should be updated when a refresh of the "Inter ID" section is required.
// This follows the query refresh pattern described at:
// https://recoiljs.org/docs/guides/asynchronous-data-queries#use-a-request-id
const intraIdSectionRefreshCountAtom = atom({
  key: "refreshIntraIdSection",
  default: 0,
});

const interIdSectionRefreshCountAtom = atom({
  key: "refreshInterIdSection",
  default: 0,
});

const currentSessionModeAtom = atom({
  key: "currentSessionMode",
  default: "intra",
  effects: [localStorageEffect("currentSessionModeAtom")],
});

const intraIdReviewStatusFilterValueAtom = atom({
  key: "intraIdReviewStatusFilterValue",
  default: NEEDS_REVIEW_STATUS,
})

const doNotAskWhenAgainWhenAcceptingMergeProposalAtom = atom({
  key: "doNotAskWhenAgainWhenAcceptingMergeProposalAtom",
  default: false,
  effects: [
    localStorageEffect("doNotAskWhenAgainWhenAcceptingMergeProposalAtom"),
  ],
});

const doNotAskAgainWhenExportingAtom = atom({
  key: "doNotAskAgainWhenExportingAtom",
  default: false,
  effects: [
    localStorageEffect("doNotAskAgainWhenExportingAtom"),
  ],
});

const imageScaleAtom = atom({
  key: 'imageScaleAtom',
  default: 100,
  effects: [
    localStorageEffect("imageScaleAtom"),
  ],
});

const pollingSessionAtom = atom({
  key: "pollingSession",
  default: false,
});

const subjectImagesCache = atomFamily({
  key: "subjectImagesCache",
  default: selectorFamily({
    key: "subjectImagesCache/default",
    get:
      (subject) =>
        async ({ get }) => {
          const currentSession = get(currentSessionAtom);
          const apiClient = get(sessionApiClientAtom);

          if (subject?.name && currentSession?.id && apiClient) {
            return await apiClient.getSubjectImages(
              currentSession.id,
              subject.name
            );
          }

          return [];
        },
  })
});

const subjectImagesCacheRefreshCount = atomFamily({
  key: "subjectImagesCacheRefreshCount",
  default: 0,
});

const subjectImagesCacheExpirationTime = atomFamily({
  key: "subjectImagesCacheExpirationTime",
  default: selectorFamily({
    key: 'subjectImagesCacheExpirationTime/default',
    get: (subject) => ({ get }) => {
      const images = get(subjectImagesCache(subject));
      return getOldestExpirationTime(images);
    },
  }),
});

const getOldestExpirationTime = (images) => {
  let oldestTime = null;
  for (let i in images) {
    const t = new Date(images[i].expirationTime);
    if (oldestTime === null || t < oldestTime) {
      oldestTime = t;
    }
  }
  return oldestTime;
}

const subjectImages = selectorFamily({
  key: "subjectImages",
  set:
    (subject) =>
      ({ get, set }, newImages) => {
        set(subjectImagesCache(subject), newImages);
        const lastRefresh = get(intraIdSectionRefreshCountAtom);
        set(subjectImagesCacheRefreshCount(subject), lastRefresh);
      },
  get:
    (subject) =>
      async ({ get }) => {
        return get(subjectImagesCache(subject));
      },
});

const hasExpired = (expirationTime) => {
  if (!expirationTime) {
    return false;
  }
  let now = new Date(Date.now());
  // Set now to a few seconds in the future to give some headroom for expiration, 
  // otherwise expiration time could occur in the middle of downloading multiple images
  now.setSeconds(now.getSeconds() + 5);
  return now >= expirationTime;
};

const useRefreshSubjectIfExpired = (subject) => {
  const expirationTime = useRecoilValue(subjectImagesCacheExpirationTime(subject));
  const refreshImages = useRecoilRefresher_UNSTABLE(subjectImagesCache(subject));
  const resetImages = useResetRecoilState(subjectImagesCache(subject));
  const count = useRecoilValue(intraIdSectionRefreshCountAtom);
  const [lastSubjectRefreshCount, setSubjectRefreshCount] = useRecoilState(
    subjectImagesCacheRefreshCount(subject)
  );

  return () => {
    if (hasExpired(expirationTime) || count !== lastSubjectRefreshCount) {
      setSubjectRefreshCount(count);
      // Refresh is required to trigger a refetch in case `set` was never called
      // on the subject. Otherwise it will use the cached default value.
      refreshImages();
      // Reset is required if `set` was called on the subject, in order to
      // reset the atom back to default. Otherwise it will always use the `set` value.
      resetImages();
    }
  }
};

const mergeProposalSubjectsWithImages = atomFamily({
  key: "mergeProposalSubjectsWithImages",
  default: selectorFamily({
    key: "mergeProposalSubjectsWithImages/default",
    get:
      ({ mergeProposal, sessionId }) =>
        async ({ get }) => {
          const apiClient = get(sessionApiClientAtom);
          get(interIdSectionRefreshCountAtom);

          if (!mergeProposal?.id || !apiClient) {
            return [];
          }

          const mp = await apiClient.getMergeProposalSubjectImages(
            sessionId,
            mergeProposal.id
          );

          return mp
        },
  }),
});

const mergeProposalSubjectsWithImagesExpirationTime = atomFamily({
  key: "mergeProposalSubjectsWithImagesExpirationTime",
  default: selectorFamily({
    key: 'mergeProposalSubjectsWithImagesExpirationTime/default',
    get: ({ mergeProposal, sessionId }) => ({ get }) => {
      const mergeProposals = get(mergeProposalSubjectsWithImages({ mergeProposal, sessionId }));

      const oldestTime = new Date(
        Math.min(
          ...mergeProposals.map((item) => {
            return item.images.map((img) => {
              return new Date(img.expirationTime)
            })
          }).flat()
        )
      );

      return oldestTime;
    },
  }),
});


const useRefreshMergeProposalSubjectsIfExpired = ({ mergeProposal, sessionId }) => {
  const expirationTime = useRecoilValue(mergeProposalSubjectsWithImagesExpirationTime({ mergeProposal, sessionId }));
  const refreshImages = useRecoilRefresher_UNSTABLE(mergeProposalSubjectsWithImages({ mergeProposal, sessionId }));
  const resetImages = useResetRecoilState(mergeProposalSubjectsWithImages({ mergeProposal, sessionId }));
  return () => {
    if (hasExpired(expirationTime)) {
      refreshImages();
      resetImages();
    }
  }
};

const useRefreshIntraIdSection = () => {
  const setIntraIdSectionRefreshCount = useSetRecoilState(
    intraIdSectionRefreshCountAtom
  );
  return () => {
    setIntraIdSectionRefreshCount((refreshCount) => refreshCount + 1);
  };
};

const useRefreshInterIdSection = () => {
  const setInterIdSectionRefreshCount = useSetRecoilState(
    interIdSectionRefreshCountAtom
  );
  return () => {
    setInterIdSectionRefreshCount((refreshCount) => refreshCount + 1);
  };
};

const isSessionActive = (currentSession) => {
  currentSession = currentSession || {};
  return (
    Object.keys(currentSession).length > 0 &&
    currentSession.status === "created"
  );
};

const isSessionErrored = (currentSession) => {
  currentSession = currentSession || {};
  return (
    Object.keys(currentSession).length > 0 && currentSession.status === "error"
  );
};

const exportsFromCurrentSessionAtom = atom({
  key: "exportsFromCurrentSessionAtom",
  default: selector({
    key: "exportsFromCurrentSessionAtom/default",
    get: ({ get }) => {
      const currentSession = get(currentSessionAtom);
      const apiClient = get(sessionApiClientAtom);

      if (apiClient && currentSession?.id) {
        return apiClient.getExports(currentSession.id);
      }

      return [];
    },
  }),
});

const allExported = (exports) => {
  return exports.every((e) => e.status === "exported");
};

export {
  currentSessionAtom,
  currentSessionModeAtom,
  sessionApiClientAtom,
  selectedSubjectAtom,
  intraIdSectionRefreshCountAtom,
  subjectImages,
  useRefreshSubjectIfExpired,
  selectedMergeProposalAtom,
  mergeProposalSubjectsWithImages,
  useRefreshMergeProposalSubjectsIfExpired,
  DEFAULT_SELECTED_MERGE_PROPOSAL,
  useRefreshIntraIdSection,
  useRefreshInterIdSection,
  isSessionActive,
  isSessionErrored,
  exportsFromCurrentSessionAtom,
  allExported,
  doNotAskWhenAgainWhenAcceptingMergeProposalAtom,
  intraIdReviewStatusFilterValueAtom,
  pollingSessionAtom,
  imageScaleAtom,
  doNotAskAgainWhenExportingAtom,
};
